import { AuthenticatedGuard } from "@/components/authenticated-guard";
import Header from "@/components/header";
import { TitleOperatorImpersonationGate } from "@/components/title/operator-impersonation-gate";
import { TitleAccountSetupCheck } from "@/components/title/title-account-setup-check";
import { TitleRequestLiveRefresh } from "@/components/title/title-request-live-refresh";
import { TitleTheme } from "@/components/title/title-theme";
import { UserType } from "@/constants";
import { titleAuthConfigured } from "@/lib/title-auth";
import { getTitleLoginConnection } from "@/lib/title-login-connection";
import { trySilentTitleAuth } from "@/lib/title-sso";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Outlet,
  createFileRoute,
  useRouterState,
} from "@tanstack/react-router";
import { useCallback } from "react";
import Auth0Provider from "../integrations/auth0/root-provider.tsx";

function TitleLayout() {
  const { user, getAccessTokenSilently } = useAuth0();
  const trySilentAuth = useCallback(
    () => trySilentTitleAuth(getAccessTokenSilently),
    [getAccessTokenSilently],
  );
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const openPath =
    pathname.startsWith("/title/auth_callback") ||
    pathname.startsWith("/title/claim");
  const page = (
    <div
      data-slot="portal-page-scroll"
      className="flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto"
    >
      <Outlet />
    </div>
  );
  return (
    <TitleTheme>
      <div className="flex h-dvh flex-col overflow-hidden bg-background">
        <Header
          user={user || null}
          showTitleHelp
          showTitleOperatorImpersonation
          blogPath="/title/blog"
        />
        <main className="mx-auto flex min-h-0 w-full max-w-[1600px] flex-1 justify-center overflow-hidden p-4 md:p-6">
          {openPath ? (
            page
          ) : (
            <AuthenticatedGuard
              allowedUserTypes={[UserType.TITLE_REQUESTOR, UserType.OPERATOR]}
              loginConnection={getTitleLoginConnection()}
              trySilentAuth={trySilentAuth}
            >
              <TitleOperatorImpersonationGate>
                <TitleRequestLiveRefresh />
                <TitleAccountSetupCheck>{page}</TitleAccountSetupCheck>
              </TitleOperatorImpersonationGate>
            </AuthenticatedGuard>
          )}
        </main>
      </div>
    </TitleTheme>
  );
}

function UnconfiguredTitleLayout() {
  return (
    <TitleTheme>
      <div className="flex min-h-dvh flex-col bg-background">
        <Header />
        <main className="mx-auto flex w-full max-w-[1600px] flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </TitleTheme>
  );
}

export const Route = createFileRoute("/title")({
  component: function TitleRoute() {
    if (!titleAuthConfigured()) {
      return <UnconfiguredTitleLayout />;
    }
    return (
      <Auth0Provider
        audience={import.meta.env.VITE_AUTH0_TITLE_AUDIENCE}
        clientId={import.meta.env.VITE_AUTH0_TITLE_CLIENT_ID}
        callbackPath="/title/auth_callback"
      >
        <TitleLayout />
      </Auth0Provider>
    );
  },
});
